import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import './App.css';

// Lazy load pages for code splitting (faster initial load)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportView = lazy(() => import('./pages/ReportView'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Valuation = lazy(() => import('./pages/Valuation'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Dividends = lazy(() => import('./pages/Dividends'));
const Screener = lazy(() => import('./pages/Screener'));
const News = lazy(() => import('./pages/News'));
const Strategy = lazy(() => import('./pages/Strategy'));
const DailyAnalysis = lazy(() => import('./pages/DailyAnalysis'));
const Chat = lazy(() => import('./pages/Chat'));
const Settings = lazy(() => import('./pages/Settings'));
const Login = lazy(() => import('./pages/Login'));

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <div className="loading-spinner"></div>
  </div>
);

function AppContent() {
  const { isAuthenticated, isLoading, passwordConfigured } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login page if not authenticated OR if password not yet configured
  if (!isAuthenticated || !passwordConfigured) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Login />
      </Suspense>
    );
  }

  // Show main app
  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:filename" element={<ReportView />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/valuation" element={<Valuation />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/dividends" element={<Dividends />} />
            <Route path="/screener" element={<Screener />} />
            <Route path="/news" element={<News />} />
            <Route path="/strategy" element={<Strategy />} />
            <Route path="/daily" element={<DailyAnalysis />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
